import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { apiUrl } from "../config/api-url";
import { TokenService } from "./token.service";
import { BehaviorSubject, Observable, of } from "rxjs";
import { tap, retry, catchError } from "rxjs/operators";
import { Vendor } from "../models/common/vendor";

@Injectable({
	providedIn: "root",
})
export class AuthService {
	private url = `${apiUrl}/auth`;
	private loggedIn = new BehaviorSubject<boolean>(this.tokenService.loggedIn());
	public authStatus = this.loggedIn.asObservable();

	private userGroup = null;

	constructor(private http: HttpClient, private tokenService: TokenService) {}

	public get trial(): boolean {
		const trial = localStorage.getItem("trial");
		if (!trial) return false;
		return trial == "yes";
	}

	/**
	 * Get the vendor name for register page in case the manufacturer is trying to sign up
	 * by following the link in the order email.
	 */
	public getRegisterPageData(data: { vendorId: number }): Observable<Vendor> {
		return this.http.get<Vendor>(`${this.url}/register-page-data?vendorId=${data.vendorId}`);
	}

	public getUserGroup(fromCache = true): Observable<{ module: "showroom" | "vendor" | "superadmin"; user_group: any }> {
		if (!fromCache || !this.userGroup || !this.userGroup.module) {
			return this.http.get<{ module: "showroom" | "vendor" | "superadmin"; user_group: any }>(`${this.url}/user-group`).pipe(
				catchError((error) => {
					return of({ module: null, user_group: null });
				}),
				tap((response) => {
					if (response.module) {
						this.userGroup = response;
					}
				})
			);
		}
		this.http.get<{ module: "showroom" | "vendor" | "superadmin"; user_group: any }>(`${this.url}/user-group`).subscribe((response) => (this.userGroup = response));
		return of(this.userGroup);
	}

	public accountVerification(token: string) {
		return this.http.post(`${this.url}/account-verification`, { token: token });
	}

	public login(data) {
		return this.http.post(`${this.url + "/login"}`, data);
	}

	public register(data) {
		return this.http.post(`${this.url + "/register"}`, data);
	}

	public getUser() {
		return this.http.post(this.url + "/me", {});
	}

	public changeAuthStatus(value: boolean) {
		this.loggedIn.next(value);
	}

	public changePassword(password: string) {
		return this.http.put(`${this.url}/change-password`, { password });
	}

	public loginAsDealer(email: string) {
		return this.http.post(`${this.url + "/login-as-dealer"}`, { email });
	}

	public loginAsVendorUser(email: string) {
		return this.http.post(`${this.url + "/login-as-vendor-user"}`, { email });
	}

	public logout() {
		this.tokenService.clear();
		this.changeAuthStatus(false);
	}

	public refresh() {
		return this.http.post(`${this.url}/refresh`, {}).pipe(
			retry(3),
			tap((response) => {
				this.tokenService.handle(response);
			})
		);
	}

	public getLogoutUrl(): Observable<string> {
		return this.http.get<string>(`${this.url}/logout-url`);
	}
}
